import { Injectable, WritableSignal, inject, signal } from "@angular/core";
import { ApiService } from "../../../shared/api.service";
import { RequestHandlerParams } from "../../../shared/types/api-service.types";
import { finalize, tap } from 'rxjs';
import { ApiResponse } from "../../../shared/common/types";
import { ORDER_STATUS_CLASSES, PAYMENT_STATUS_CLASSES } from "../order-and-billing/order-and-billing.constants";
import { InvoiceHistory, InvoiceHistoryResponse } from "./invoice-history.types";

@Injectable()
export class InvoiceHistoryService {
    private apiService = inject(ApiService);

    private endpoints = {
        base: '/account/invoice-history',
    };

    invoiceHistory: WritableSignal<InvoiceHistory[]> = signal([]);
    invoicesLength: WritableSignal<number> = signal(0);
    /** Starts true so the modal renders its loading state on first open instead
     *  of flashing the "no invoice history" empty message while the call is in
     *  flight. */
    isLoading: WritableSignal<boolean> = signal(true);

    getInvoiceHistory(page: number, length: number, pastDueOnly = false) {
        // `pastDueOnly=true` asks the backend for only the unpaid invoices past
        // the customer's notification threshold — the same set the past-due
        // popup and cart banner are built around.
        const qs = `page=${page}&len=${length}` + (pastDueOnly ? '&pastDueOnly=true' : '');

        const params: RequestHandlerParams = {
            endpoint: `${this.endpoints.base}?${qs}`,
            method: 'GET',
            apiV3: true,
            showErrorMessage: false
        };

        this.isLoading.set(true);

        this.apiService.handleRequest<ApiResponse<InvoiceHistoryResponse>>(params).pipe(
            tap((response: ApiResponse<InvoiceHistoryResponse>) => this.setUpInvoiceHistoryData(response.data)),
            finalize(() => this.isLoading.set(false))
        ).subscribe();
    }

    private setUpInvoiceHistoryData(data: InvoiceHistoryResponse) {
        const invoices = data.invoices;
        this.invoiceHistory.set(invoices.map(e => {
            e.paymentStatusClass = PAYMENT_STATUS_CLASSES[e.paymentStatus?.toLowerCase()];
            return e;
        }));

        this.invoicesLength.set(data.length);
    }

    /** Resolves the invoice's `invoiceURL`. Opening it belongs to the component: the tab has to
     *  be opened inside the click, before this request is even sent (DEV-11537). */
    getInvoiceDetails(id: number) {
        const params: RequestHandlerParams = {
            endpoint: `${this.endpoints.base}/${id}`,
            method: 'GET',
            apiV3: true,
            showErrorMessage: false
        };

        return this.apiService.handleRequest<ApiResponse<{ invoiceURL: string }>>(params);
    }

}
